/**
 * Pure utils and constants for the Products form.
 * No React dependencies – safe to reuse and test in isolation.
 * @typedef {import('../../../models/Product').Product} Product
 */

import { PRODUCTS_QUERY_KEY as PK, CATEGORIES_QUERY_KEY as CK, LIST_STALE_TIME_MS } from '../../common/utils/queryKeys';

/** Query key for products list (use with useQuery / invalidateQueries) */
export const PRODUCTS_QUERY_KEY = PK;
/** Query key for product categories (used when Category field is focused) */
export const PRODUCT_CATEGORIES_QUERY_KEY = CK;
export { LIST_STALE_TIME_MS };

/** condition_enum: new | used */
export const CONDITION_OPTIONS = [
  { value: '', label: '—' },
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' }
];

/** warranty_enum options */
export const WARRANTY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'Garantia do vendedor', label: 'Garantia do vendedor' },
  { value: 'Garantia de Fábrica', label: 'Garantia de Fábrica' },
  { value: 'Sem garantia', label: 'Sem garantia' }
];

/** Product pictures: limits from Mercado Livre (JPG/PNG, 10 MB). Supabase free allows 50 MB. */
export const PICTURE_ACCEPT = 'image/jpeg,image/jpg,image/png';
export const PICTURE_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const PICTURE_MAX_SIZE_LABEL = '10 MB';

export const emptyFormData = () => ({
  name: '',
  brand: '',
  product_model: '',
  description: '',
  sku: '',
  NCM: '',
  product_universal_code: '',
  category_id: null,
  sub_category_id: null,
  categoryDisplayName: '',
  subCategoryDisplayName: '',
  condition: '',
  warranty: ''
});

/** @param {Product} product */
export const productToFormData = (product) => ({
  name: product.name ?? '',
  brand: product.brand ?? '',
  product_model: product.product_model ?? '',
  description: product.description ?? '',
  sku: product.sku ?? '',
  NCM: product.NCM ?? '',
  product_universal_code: product.product_universal_code ?? '',
  category_id: product.category_id ?? null,
  sub_category_id: product.sub_category_id ?? null,
  categoryDisplayName: '',
  subCategoryDisplayName: '',
  condition: product.condition ?? '',
  warranty: product.warranty ?? ''
});

/**
 * Map MLB category attributes API response to additional-detail rows (create mode).
 * @param {Array<{ attribute_id: string, name?: string, values?: Array<{ value_id: string, value_name?: string, value_kind?: string }> }>} attrs
 * @returns {Array<{ attribute_id: string, mlb_attribute_id: string, attribute_name: string, detail_group: string, detail_name: string, detail_value: string, allowed_values: Array<{ value_id: string, value_name: string }> }>}
 */
export function additionalDetailRowsFromMlbAttributes(attrs) {
  if (!Array.isArray(attrs) || attrs.length === 0) return [];
  return attrs.map((a) => {
    const allowedValues = (a.values || [])
      .filter((v) => v.value_kind === 'value')
      .map((v) => ({ value_id: v.value_id, value_name: v.value_name || '' }));
    return {
      attribute_id: a.attribute_id,
      mlb_attribute_id: a.attribute_id,
      attribute_name: a.name || '',
      detail_group: '',
      detail_name: '',
      detail_value: '',
      allowed_values: allowedValues
    };
  });
}

/**
 * Enrich additional-detail rows with attribute names from MLB attributes.
 * @param {Array<{ mlb_attribute_id?: string, attribute_id?: string, attribute_name?: string }>} rows
 * @param {Array<{ attribute_id: string, name?: string }>} attrs
 */
export function enrichAdditionalDetailRowsWithMlbNames(rows, attrs) {
  const nameMap = {};
  (attrs || []).forEach((a) => {
    nameMap[a.attribute_id] = a.name || '';
  });
  return rows.map((d) => ({
    ...d,
    attribute_name: nameMap[d.mlb_attribute_id || d.attribute_id] || d.attribute_name || ''
  }));
}

/**
 * Merge new MLB attribute rows into existing additional details (edit mode): enrich existing and append new.
 * @param {Array} prev - current additional details
 * @param {Array} attrs - MLB attributes from API
 * @returns {Array} merged rows
 */
export function mergeAdditionalDetailsWithMlbAttrs(prev, attrs) {
  const existingIds = new Set(prev.map((d) => d.attribute_id || d.mlb_attribute_id).filter(Boolean));
  const nameMap = {};
  attrs.forEach((a) => {
    nameMap[a.attribute_id] = a.name || '';
  });
  const newRows = additionalDetailRowsFromMlbAttributes(
    attrs.filter((a) => !existingIds.has(a.attribute_id))
  );
  const enriched = prev.map((row) => {
    const attrId = row.attribute_id ?? row.mlb_attribute_id;
    const a = attrs.find((attr) => attr.attribute_id === attrId);
    const allowedValues =
      row.allowed_values?.length > 0
        ? row.allowed_values
        : (a?.values || [])
            .filter((v) => v.value_kind === 'value')
            .map((v) => ({ value_id: v.value_id, value_name: v.value_name || '' }));
    return {
      ...row,
      attribute_id: attrId,
      attribute_name: row.attribute_name || nameMap[attrId] || '',
      allowed_values: allowedValues
    };
  });
  return [...enriched, ...newRows];
}
